import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Building2, BarChart3, FileSearch, TrendingUp } from "lucide-react";
import { format, subDays, startOfDay } from "date-fns";
import { useFactory } from "@/core/hooks/useFactory";
import { useLanguage } from "@/core/LanguageContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import {
  fetchSupplierIncomingResultsByFactory,
  fetchSuppliersByFactory,
  type SupplierIncomingResult,
} from "../api";
import { KpiCards } from "../components/KpiCards";
import { OverviewTab } from "../components/OverviewTab";
import { SpcTab } from "../components/SpcTab";
import { SuppliersTab } from "../components/SuppliersTab";
import { InspectionsTab } from "../components/InspectionsTab";
import { InspectionDetailDialog } from "../components/InspectionDetailDialog";

export default function SupplierQualityPage() {
  const { selectedFactoryId, selectedFactory } = useFactory();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();

  const [timeRange, setTimeRange] = useState<"7d" | "30d" | "90d" | "all">("30d");
  const [selectedSupplier, setSelectedSupplier] = useState("all");
  const [selectedParameter, setSelectedParameter] = useState("");
  const [viewingInspection, setViewingInspection] = useState<SupplierIncomingResult | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const activeTab = ["overview", "spc", "suppliers", "inspections"].includes(
    searchParams.get("tab") ?? "",
  )
    ? (searchParams.get("tab") as "overview" | "spc" | "suppliers" | "inspections")
    : "overview";

  const updateTab = (value: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", value);
    setSearchParams(nextParams);
  };

  const tenantId = selectedFactory?.tenant_id ?? null;

  const suppliersQueryKey = [
    "mod_8d",
    "suppliers",
    tenantId,
    selectedFactoryId,
  ] as const;

  const { data: suppliers = [] } = useQuery({
    queryKey: suppliersQueryKey,
    queryFn: () => fetchSuppliersByFactory(tenantId!, selectedFactoryId!),
    enabled: !!selectedFactoryId && !!tenantId,
  });

  const getDateFilter = () => {
    if (timeRange === "all") return null;
    const days = timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : 90;
    return startOfDay(subDays(new Date(), days)).toISOString();
  };

  const { data: inspections = [], isLoading } = useQuery({
    queryKey: [
      "mod_supplier_incoming",
      "inspection-results",
      tenantId,
      selectedFactoryId,
      timeRange,
      selectedSupplier,
    ],
    queryFn: () =>
      fetchSupplierIncomingResultsByFactory(tenantId!, selectedFactoryId!, {
        sinceIso: getDateFilter() ?? undefined,
        supplierId: selectedSupplier !== "all" ? selectedSupplier : undefined,
      }),
    enabled: !!selectedFactoryId && !!tenantId,
  });

  // Derived metrics
  const totalInspections = inspections.length;
  const approvedCount = inspections.filter((i) => i.overall_status === "approved").length;
  const rejectedCount = inspections.filter((i) => i.overall_status === "rejected").length;
  const approvalRate =
    totalInspections > 0 ? ((approvedCount / totalInspections) * 100).toFixed(1) : "0";

  // Trend data
  const trendData = (() => {
    const days =
      timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : timeRange === "90d" ? 90 : 365;
    const data: { date: string; approved: number; rejected: number; rate: number }[] = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = subDays(new Date(), i);
      const dateStr = format(date, "yyyy-MM-dd");
      const dayInspections = inspections.filter(
        (ins) => format(new Date(ins.created_at), "yyyy-MM-dd") === dateStr,
      );
      const approved = dayInspections.filter((i) => i.overall_status === "approved").length;
      const rejected = dayInspections.filter((i) => i.overall_status === "rejected").length;
      const total = approved + rejected;
      data.push({
        date: format(date, "MMM d"),
        approved,
        rejected,
        rate: total > 0 ? (approved / total) * 100 : 0,
      });
    }

    if (days > 30) {
      const aggregated: typeof data = [];
      const step = Math.ceil(days / 30);
      for (let i = 0; i < data.length; i += step) {
        const slice = data.slice(i, i + step);
        const approved = slice.reduce((s, d) => s + d.approved, 0);
        const rejected = slice.reduce((s, d) => s + d.rejected, 0);
        const total = approved + rejected;
        aggregated.push({
          date: slice[0].date,
          approved,
          rejected,
          rate: total > 0 ? (approved / total) * 100 : 0,
        });
      }
      return aggregated;
    }
    return data;
  })();

  // Pie data
  const pieData = [
    { name: "Approved", value: approvedCount, color: "var(--success)" },
    { name: "Rejected", value: rejectedCount, color: "var(--destructive)" },
  ].filter((d) => d.value > 0);

  // SPC parameters
  const testParameters = (() => {
    const params = new Set<string>();
    inspections.forEach((ins) => {
      ins.test_results.forEach((result) => {
        if (result.name) params.add(result.name);
      });
    });
    return Array.from(params);
  })();

  // SPC data for selected parameter
  const spcData = (() => {
    if (!selectedParameter) return [];
    return inspections
      .map((ins) => {
        const result = ins.test_results.find((entry) => entry.name === selectedParameter);
        if (!result) return null;
        const numericValue = result.numeric_value ?? parseFloat(result.value);
        if (isNaN(numericValue)) return null;
        return {
          date: format(new Date(ins.created_at), "MMM d HH:mm"),
          value: numericValue,
          ucl: result.ucl,
          lcl: result.lcl,
          supplier: ins.supplier_name,
          batch: ins.batch_lot_number,
          passed: result.passed,
        };
      })
      .filter(Boolean)
      .reverse()
      .slice(-50);
  })();

  // SPC stats
  const spcStats = (() => {
    if (spcData.length === 0) return null;
    const values = spcData.map((d) => d!.value);
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const stdDev = Math.sqrt(
      values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length,
    );
    const ucl = spcData[0]?.ucl ?? mean + 3 * stdDev;
    const lcl = spcData[0]?.lcl ?? mean - 3 * stdDev;
    return { mean, stdDev, ucl, lcl, min: Math.min(...values), max: Math.max(...values) };
  })();

  const openInspectionDetail = (inspection: SupplierIncomingResult) => {
    setViewingInspection(inspection);
    setDetailDialogOpen(true);
  };

  if (!selectedFactoryId) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Please select a factory first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground flex items-center gap-2">
            <Building2 className="w-6 h-6 text-primary" />
            {t("mod_8d.supplierQuality.title", "Supplier Quality")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t(
              "mod_8d.supplierQuality.subtitle",
              "Incoming inspection analytics and SPC monitoring",
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Select
            value={timeRange}
            onValueChange={(v) => setTimeRange(v as "7d" | "30d" | "90d" | "all")}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>

          <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Suppliers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Suppliers</SelectItem>
              {suppliers.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.code} - {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      <KpiCards
        totalInspections={totalInspections}
        approvalRate={approvalRate}
        rejectedCount={rejectedCount}
        supplierCount={suppliers.length}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={updateTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 lg:w-[600px]">
          <TabsTrigger value="overview" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="spc" className="gap-2">
            <TrendingUp className="w-4 h-4" />
            <span className="hidden sm:inline">SPC Charts</span>
          </TabsTrigger>
          <TabsTrigger value="suppliers" className="gap-2">
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">Suppliers</span>
          </TabsTrigger>
          <TabsTrigger value="inspections" className="gap-2">
            <FileSearch className="w-4 h-4" />
            <span className="hidden sm:inline">Incoming Inspection</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <OverviewTab
            inspections={inspections}
            trendData={trendData}
            pieData={pieData}
          />
        </TabsContent>

        <TabsContent value="spc" className="space-y-4">
          <SpcTab
            testParameters={testParameters}
            selectedParameter={selectedParameter}
            onParameterChange={setSelectedParameter}
            spcData={spcData}
            spcStats={spcStats}
          />
        </TabsContent>

        <TabsContent value="suppliers" className="space-y-4">
          {tenantId && (
            <SuppliersTab
              suppliers={suppliers}
              suppliersQueryKey={suppliersQueryKey}
              tenantId={tenantId}
              factoryId={selectedFactoryId}
            />
          )}
        </TabsContent>

        <TabsContent value="inspections" className="space-y-4">
          {tenantId && (
            <InspectionsTab
              suppliers={suppliers}
              tenantId={tenantId}
              factoryId={selectedFactoryId}
              onViewInspection={openInspectionDetail}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Inspection Detail Dialog */}
      <InspectionDetailDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        inspection={viewingInspection}
      />
    </div>
  );
}
